import React, { useEffect, useRef } from 'react';

// AS-S2 + AS-S2.1: per-user Effort grid preferences (localStorage).
//
// Only one toggle remains after AS-S2.1: tooltips on/off. The other AS-S2
// toggles (statusBorders, emptyCellAffordance) were removed — those features
// are now always-on. Old localStorage keys are ignored on read; they are
// not migrated.

export const DEFAULT_EFFORT_PREFS = {
  tooltipsEnabled: true,
};

export const PREFS_STORAGE_KEY = 'hrapp.effortGridPrefs';

export function loadEffortPrefs() {
  if (typeof window === 'undefined') return DEFAULT_EFFORT_PREFS;
  try {
    const raw = window.localStorage.getItem(PREFS_STORAGE_KEY);
    if (!raw) return DEFAULT_EFFORT_PREFS;
    const parsed = JSON.parse(raw);
    // Only the supported key is taken. Older keys (statusBorders /
    // emptyCellAffordance) are silently dropped — they aren't migrated.
    return {
      tooltipsEnabled: typeof parsed?.tooltipsEnabled === 'boolean'
        ? parsed.tooltipsEnabled
        : DEFAULT_EFFORT_PREFS.tooltipsEnabled,
    };
  } catch {
    return DEFAULT_EFFORT_PREFS;
  }
}

export function saveEffortPrefs(prefs) {
  if (typeof window === 'undefined') return;
  try {
    // Persist only the supported keys.
    window.localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify({
      tooltipsEnabled: !!prefs.tooltipsEnabled,
    }));
  } catch { /* ignore quota / private-mode errors */ }
}

const EffortGridPrefs = ({ open, anchorRef, prefs, onChange, onClose }) => {
  const ref = useRef(null);

  // Click outside closes.
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)
          && anchorRef?.current && !anchorRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open, onClose, anchorRef]);

  if (!open) return null;

  const set = (key) => (e) => onChange({ ...prefs, [key]: e.target.checked });

  const rect = anchorRef?.current?.getBoundingClientRect();
  const style = rect
    ? { top: rect.bottom + 6, left: Math.max(8, rect.right - 280) }
    : { top: 64, right: 16 };

  return (
    <div
      ref={ref}
      className="pdp-prefs-popover"
      style={{ position: 'fixed', ...style }}
      data-testid="effort-prefs-popover"
      role="dialog"
      aria-label="Effort grid preferences"
    >
      <h4>⚙ View preferences</h4>
      <label>
        <input
          type="checkbox"
          checked={!!prefs.tooltipsEnabled}
          onChange={set('tooltipsEnabled')}
          data-testid="effort-prefs-tooltips"
        />
        Show hover tooltips on cost cells
      </label>
      <p className="hint">Saved per browser (localStorage).</p>
    </div>
  );
};

export default EffortGridPrefs;
